import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AlarmSettingsBase } from '../../../../pages/notifications/components/alarm-settings/alarm-settings-base.class';
import { AlarmSettingsFormControls } from '../../../../pages/notifications/components/alarm-settings/contract/alarm-settings-form-controls.contract';
import { AlarmSettingsFieldData } from '../../../../pages/notifications/contract/alarm-settings-field-data.contract';
import { ErrorComponent } from '../form-field/components/error/error.component';
import { LabelComponent } from '../form-field/components/label/label.component';
import { FormFieldComponent } from '../form-field/form-field.component';
import { InputNumberComponent } from '../input-number/input-number.component';
import { InputTextComponent } from '../input-text/input-text.component';
import { TimefenceWeekComponent } from '../timefence/timefence-week/timefence-week.component';

@Component({
  selector: 'osp-timefencing-settings',
  standalone: true,
  imports: [
    TimefenceWeekComponent,
    InputTextComponent,
    ReactiveFormsModule,
    InputNumberComponent,
    TranslateModule,
    FormFieldComponent,
    LabelComponent,
    ErrorComponent,
  ],
  templateUrl: './timefencing-settings.component.html',
  styleUrls: ['./timefencing-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimefencingSettingsComponent extends AlarmSettingsBase implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() set form(form: FormGroup<AlarmSettingsFormControls>) {
    this.settingsForm = form;
    this.handleTimefenceWeekErrors();
  }

  @Input() set fieldData(fieldData: AlarmSettingsFieldData) {
    this.alarmSettingsFieldData = fieldData;
  }

  protected $timefenceWeekErrors = signal(
    this.settingsForm?.get(this.controlNames.SETTINGS_TIMEFENCING_WEEK_DAY)?.errors,
  );

  protected tooltipPosition = 'top';

  protected toggleOperatingHoursInput = new FormControl<boolean>(true);
  protected toggleDistanceInput = new FormControl<boolean>(true);

  protected operatingHoursInputLabel = '';
  protected operatingHoursInputExplanation: string = '';

  protected distanceInputLabel = '';
  protected distanceInputExplanation: string = '';

  private toggleChangeListener(
    firstToggle: FormControl<boolean | null>,
    firstInput: AbstractControl | null,
    secondToggle: FormControl<boolean | null>,
    secondInput: AbstractControl | null,
  ) {
    firstToggle.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: unknown) => {
      if (value) {
        firstInput?.enable();
      } else {
        firstInput?.disable();
      }

      if (!value && !secondToggle.value) {
        secondToggle.setValue(true, { emitEvent: false });
        secondInput?.enable();
      }
    });
  }

  ngOnInit(): void {
    this.setExplanations();
    this.setLabels();
    this.toggleChangeListener(
      this.toggleOperatingHoursInput,
      this.settingsForm.get(this.controlNames.SETTINGS_TIMEFENCING_HOURS),
      this.toggleDistanceInput,
      this.settingsForm.get(this.controlNames.SETTINGS_TIMEFENCING_DISTANCE),
    );
    this.toggleChangeListener(
      this.toggleDistanceInput,
      this.settingsForm.get(this.controlNames.SETTINGS_TIMEFENCING_DISTANCE),
      this.toggleOperatingHoursInput,
      this.settingsForm.get(this.controlNames.SETTINGS_TIMEFENCING_HOURS),
    );
    this.setInitialSwitchValues();
  }

  private setExplanations(): void {
    this.operatingHoursInputExplanation = this.getFieldTooltipText(this.controlNames.SETTINGS_TIMEFENCING_HOURS);
    this.distanceInputExplanation = this.getFieldTooltipText(this.controlNames.SETTINGS_TIMEFENCING_DISTANCE);
  }

  private setLabels(): void {
    this.operatingHoursInputLabel = this.getFieldLabel(this.controlNames.SETTINGS_TIMEFENCING_HOURS);
    this.distanceInputLabel = this.getFieldLabel(this.controlNames.SETTINGS_TIMEFENCING_DISTANCE);
  }

  private setInitialSwitchValues() {
    const operatingHours = this.settingsForm.get(this.controlNames.SETTINGS_TIMEFENCING_HOURS)?.value;
    const distance = this.settingsForm.get(this.controlNames.SETTINGS_TIMEFENCING_DISTANCE)?.value;

    this.toggleOperatingHoursInput.setValue(!!operatingHours);
    this.toggleDistanceInput.setValue(!!distance);
  }

  private handleTimefenceWeekErrors(): void {
    const timefenceControl = this.settingsForm.get(this.controlNames.SETTINGS_TIMEFENCING_WEEK_DAY);

    timefenceControl?.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.$timefenceWeekErrors.set(timefenceControl.errors));
  }
}
